import {
  compose,
  map,
  values,
  reduce,
  curry,
  keys,
  groupBy,
  uniq,
  fromPairs,
  toPairs,
  take,
  filter,
  propEq,
} from 'ramda';
import CSSCOLORSJSON from '../data/css-color-names.json';

export const colors = compose(values)(CSSCOLORSJSON);

/**
 * Group By provided id param
 */
const groupById = (id: any) =>
  groupBy((item: { [x: string]: any }) => item[`${id}`]);

/**
 * Within a set of rows if passed a set of keys (attributes from the rows)
 * Sum them each key for all the rows
 */
export const sumRowsByKeys = ({ rows, keys }: any) => {
  const data = compose(
    map((i: { count: number }) => +i.count.toFixed(2)),
    values,
    map((i: string | number) => {
      return {
        id: i,
        count: reduce(
          (a: any, b: { [x: string]: any }) => a + (b[i] || 0),
          0
        )(rows),
      };
    })
  )(keys);
  return { data, labels: keys };
};

export const getKeySpecificData = curry(
  (selectedList: any, keys: any, rows: any[], select: string | number) => {
    return compose(
      map((key: any) => {
        return {
          label: key,
          data: compose(
            map((item: { count: any }) => item.count),
            map((selectedItem: any) => {
              const filteredSelection = rows.filter(
                (i: { [x: string]: any }) => i[select] === selectedItem
              );
              return {
                id: key,
                [select]: selectedItem,
                count: reduce((a: any, b: { [x: string]: any }) => {
                  return a + (b[`${key}`] || 0);
                }, 0)(filteredSelection),
              };
            })
          )(selectedList),
        };
      })
    )(keys);
  }
);

export const getStackedValues = (
  rows: any,
  keys: any,
  select: string | number
) => {
  const selectedList = compose(
    uniq,
    map((i: { [x: string]: any }) => i[select])
  )(rows);
  return {
    labels: selectedList,
    data: getKeySpecificData(selectedList, keys, rows, select),
  };
};

export const sumByIdGroupBySelect = ({ rows, keys, select }: any) => {
  const { labels, data } = getStackedValues(rows, keys, select);
  return { labels, data };
};

const tranformToStackedBarChart = curry(
  (prepData: { labels: any; data: any }) => {
    const { labels, data } = prepData;
    const datasets = data.map(
      (i: { label: any; data: any }, index: number) => ({
        label: i.label,
        data: i.data,
        backgroundColor: colors[index],
        hoverBackgroundColor: colors[index],
        barThickness: 10,
      })
    );

    return {
      labels,
      datasets,
    };
  }
);

const transformToPie = curry((prepData: { labels: any; data: any }) => {
  const { labels, data } = prepData;
  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: colors,
      },
    ],
  };
});

const tranformsToBar = curry(
  (
    prepData: { labels: any; data: any },
    options: { title: any; colors: any }
  ) => {
    const { labels, data } = prepData;
    return {
      labels,
      datasets: [
        {
          data,
          label: options.title,
          backgroundColor: options.colors,
        },
      ],
    };
  }
);

export const groupByIDAndGetCount = ({ groupBy, rows }: any) => {
  const data = compose(
    map((i: string | any[]) => i.length),
    values,
    groupById(groupBy)
  )(rows);
  const labels = compose(keys, groupById(groupBy))(rows);
  return { data, labels };
};

export const getSelectedCount = curry(
  (select: any, i: { [x: string]: string | any[] }) => {
    return i[`${select}`] ? i[`${select}`].length : 0;
  }
);

export const hasElement = curry((select: any, obj: any[]) => {
  return obj[0] === select;
});

export const trimValues = curry(
  (groupBy: any, obj: { [x: string]: string }) => {
    if (typeof obj[`${groupBy}`] === 'string') {
      return { ...obj, [groupBy]: obj[`${groupBy}`].trim() };
    } else {
      return obj;
    }
  }
);

/**
 * This is for single card where the return is the ID - The value count interested in
 * and group by is where we get the attribute on which the count is taken for the selection (select)
 * For ex: Count (operation) the number where the gender (groupBy) is 'male' (select)
 * One dimension count (ACROSS ROW DIMENSION)
 * Group By for specific Value (select)
 */
export const valueAcrossRowsSelectCountGroupByID = ({
  groupBy,
  rows,
  select,
}: any) => {
  return {
    id: select,
    count: compose(
      getSelectedCount(select),
      fromPairs,
      take(1),
      filter(hasElement(select)),
      toPairs,
      groupById(groupBy),
      map(trimValues(groupBy))
    )(rows),
  };
};

const stackedBarGroupByIdTransform = compose(sumByIdGroupBySelect);

const filterBySelect = (select: any) => compose(filter, propEq(`${select}`));

const tranformsToMultiBar = (prepData: { labels: any; data: any }) => {
  const { labels, data } = prepData;
  const datasets = data.map((item: any, i: string | number) => ({
    ...item,
    backgroundColor: colors[i],
  }));
  return {
    labels,
    datasets,
  };
};

const applyFilters = curry((filters: any[], original: any) => {
  let filteredData = [...original];
  // console.log(filteredData, filters);
  if (filters) {
    filters.map((filter: { id: any; value: any }) => {
      filteredData = filteredData.filter((item) => {
        return item[`${filter.id}`] === filter.value;
      });
      return null;
    });
  }
  return filteredData;
});

const multiBarCountById = ({ groupBy, select, rows, keys, filters }: any) => {
  const filteredRows = compose(applyFilters(filters))(rows);
  const selectedList = compose(
    uniq,
    map((i: { [x: string]: any }) => i[select])
  )(filteredRows);
  const groupWiseKeysCount = map((el: any) => {
    const filterSelect = filterBySelect(select);
    const filteredData = filterSelect(el)(rows);
    const groupedData = compose(
      map((d: string | any[]) => {
        return d.length;
      }),
      groupById(groupBy)
    )(filteredData);
    return { id: el, ...groupedData };
  })(selectedList);

  const getCount = (_: any, key: any, val: any) => {
    return val[`${key}`] ? val[`${key}`] : 0;
  };

  const keyWiseSelectList = map((key: any) => {
    const label = key;
    const data = map((select: any) => {
      const filteredVal = groupWiseKeysCount.find(
        (i: { id: any }) => i.id === select
      );
      return getCount(select, key, filteredVal);
    })(selectedList);
    return { data, label };
  })(keys);
  return { labels: selectedList, data: keyWiseSelectList };
};

/**
 * Generate pie chart data where you want to count based on the groupBy attribute
 * One dimension count (ACROSS ROW DIMENSION)
 * Group By
 */
export const pieChartAcrossRowsCountGroupById = compose(
  transformToPie,
  groupByIDAndGetCount
);

/**
 * One Dimension attributes across rows (ACROSS ROW DIMENSION)
 * Group By Count
 * Generate bar chart data where you want to count based on the groupBy attribute
 */
export const barChartAcrossRowsCountGroupById = compose(
  tranformsToBar,
  groupByIDAndGetCount
);

/**
 * One Dimension (WITHIN ROW DIMENSION)
 * Based on Attributes of each row provided as keys
 * Pie Chart where this is grouped by the keys and summed
 */
export const pieChartWithinRowSumByKeys = compose(
  transformToPie,
  sumRowsByKeys
);

/**
 * Two dimensions (WITHIN ROW DIMENSION)
 * Y-Axis is the selection
 * X-Axis is the stacked sum of values for each key of the selection
 * Stacked Bar Chart
 * Where the y-axis is the 'select'
 * and the stacks are based on the sum of the data attributes provided by the 'keys' for the select
 */
export const stackedBarChartWithinRowSelectSumByKeys = compose(
  tranformToStackedBarChart,
  stackedBarGroupByIdTransform
);

/**
 * Multiple Bar Chart based on a y-axis - select and x-axis multi groups based on groupBy
 * Attributes per row based dimensions (WITHIN ROW DIMENSION)
 * Two dimensions where the all possible values may not be present
 * in the grouped attribute , hence keys are used to add the missing attributes
 * with a count
 * 1 -> Y Axis - List of selected values for 'select'
 * 2 -> Group By - an attribute and get the count but add count for all possible values (0 if not present)
 */
export const multiBarChartWithinRowSelectCountForKeysGroupByID = compose(
  tranformsToMultiBar,
  multiBarCountById
);
